import React, { useRef, useState } from "react";
import { ImageBackground, PixelRatio, StyleSheet, View, Text, Platform, TouchableOpacity, Image } from "react-native";
import { background, Icons } from "../../assets";
import LayoutWrapper from "../../components/LayoutWrapper/LayoutWrapper";
import { useNavigation } from "@react-navigation/native";
import { useGlobalState } from "../../shared/state";
import { I18N } from "../../shared/util";
import { Fonts, Styles } from "../../styles";

const NewScanUserBirthYear: React.FC = (): JSX.Element => {
  const [, setRoute] = useGlobalState("currentScreen");
  const [currentScanner] = useGlobalState("currentScanner");
  const [, setUserBirthDate] = useGlobalState("userBirthDate");
  const [language] = useGlobalState("language");
  const [startYear, setStartYear] = useState(1980); // decade start
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const isBirkenstock = currentScanner && currentScanner.partner && currentScanner.partner?.toLowerCase() === "birkenstock";

  const ArrowButton = ({ onPress, disabled, direction }: any) => (
    <TouchableOpacity disabled={disabled} onPress={onPress} style={[styles.arrowButton, disabled && styles.disabled]}>
      <Image source={Icons.backArrowIconBirkenstock} style={[styles.arrowIcon, direction === "right" && { transform: [{ rotate: "180deg" }] }]} resizeMode="contain" />
    </TouchableOpacity>
  );

  const CURRENT_YEAR = new Date().getFullYear();
  const MIN_YEAR = 1900; // example
  const MAX_YEAR = CURRENT_YEAR;

  const canGoPrev = startYear - 10 >= MIN_YEAR;
  const canGoNext = startYear + 10 <= MAX_YEAR;

  const years = Array.from({ length: 10 }, (_, i) => startYear + i);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onSelectYear = (year: number) => {
    if (year < MIN_YEAR || year > MAX_YEAR) return;
    setSelectedYear(year);
    setUserBirthDate(year + "-01-01");
    timeoutRef.current = setTimeout(() => {
      navigate("NewScanModeSelection");
    }, 500);
  };

  const [value, setValue] = useState<string>("2020-01-01");

  const i18n = I18N(language);
  const navigation = useNavigation();

  const navigate = (route: string | undefined) => {
    if (route) {
      navigation.navigate(route);
      setRoute(route);
    }
  };

  const onclick = () => {
    setUserBirthDate(value);
    navigate("NewScanModeSelection");
  };

  return (
    <ImageBackground source={!isBirkenstock && background} style={Styles.backgroundImage} blurRadius={15}>
      <LayoutWrapper
        showHeaderCloseButton={false}
        showHeaderLogo={false}
        showMenuButtons={false}
        showStartButton={false}
        hideNewScanButton
        hideInfoButton
        title={i18n.t("newScan.birthdate")}
        showBirkenstockFooter={isBirkenstock && true}
        showBirkenstockFooterLogo={isBirkenstock && true}
        showBirkenstockHomeButton={isBirkenstock && true}
      >
        <View style={styles.contentWrapper}>
          <View style={styles.cardWrapper}>
            <View style={styles.header}>
              <View style={styles.header}>
                <ArrowButton direction="left" disabled={!canGoPrev} onPress={() => setStartYear(startYear - 10)} />

                <Text style={[styles.rangeText, {fontFamily: isBirkenstock && "Geist-Light"}]}>{`${startYear} - ${startYear + 9}`}</Text>

                <ArrowButton direction="right" disabled={!canGoNext} onPress={() => setStartYear(startYear + 10)} />
              </View>
            </View>

            {/* Year Grid */}
            <View style={styles.grid}>
              {years.map((year) => {
                const isSelected = year === selectedYear;
                const isDisabled = year < MIN_YEAR || year > MAX_YEAR;

                return (
                  <TouchableOpacity
                    key={year}
                    disabled={isDisabled}
                    style={[styles.yearButton, isSelected && styles.selectedYear, isDisabled && styles.disabledYear]}
                    onPress={() => onSelectYear(year)}
                  >
                    <Text style={[styles.yearText, isSelected && styles.selectedYearText, isDisabled && styles.disabledYearText, { fontFamily: "Birkentype", fontSize: 36 }]}>{year}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>
      </LayoutWrapper>
    </ImageBackground>
  );
};

export default NewScanUserBirthYear;

const styles = StyleSheet.create({
  backgroundImage: {
    resizeMode: "cover",
    flex: 1,
    backgroundColor: "white",
  },
  contentWrapper: {
    flex: 1,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  cardWrapper: {
    flex: 1,
    minWidth: "60%",
    maxWidth: "70%",
    minHeight: "60%",
    maxHeight: "70%",
    padding: "1.5%",
    marginTop: "-5%",
    backgroundColor: "#ffffff",
    opacity: 0.87,
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "space-between",
    shadowColor: "#000",
    shadowOffset: { width: 6 / PixelRatio.get(), height: 6 / PixelRatio.get() },
    shadowOpacity: 0.16,
    shadowRadius: 4 / PixelRatio.get(),
    elevation: 1,
  },
  picker: {
    backgroundColor: "white",
    width: Platform.OS === "web" ? "60%" : "100%",
    height: Platform.OS === "web" ? "15" : "50%",
    marginBottom: Platform.OS === "web" ? "20%" : undefined,
  },
  optionCard: {
    width: "auto",
    height: "auto",
    borderRadius: 5,
    borderWidth: 0.5,
    marginRight: "1%",
    marginTop: "1%",
    borderColor: "black",
    alignContent: "center",
    alignItems: "center",
    padding: "1%",
  },

  optionCardCaption: {
    fontFamily: "Montserrat-Light",
    fontSize: 16,
    textAlign: "center",
    alignSelf: "center",
  },
  optionCardCaptionActive: {
    fontFamily: "Montserrat-Light",
    fontSize: 16,
    textAlign: "center",
    color: "#2bc6ff",
  },
  textInputStyle: {
    width: 250,
    backgroundColor: "#dde8c9",
    padding: 16,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },

  arrow: {
    fontSize: 28,
    paddingHorizontal: 10,
  },

  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    width: "100%",
  },

  yearButton: {
    width: "12%",
    paddingVertical: 14,
    marginHorizontal: "3%",
    marginBottom: 12,
    borderRadius: 12,
    backgroundColor: "#E0E0E0",
    alignItems: "center",
  },

  yearText: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#555",
  },

  selectedYear: {
    backgroundColor: "#0B2C8F",
  },

  selectedYearText: {
    color: "#FFF",
  },
  disabled: {
    opacity: 0.3,
  },

  disabledYear: {
    backgroundColor: "#d1d3d4",
  },

  disabledYearText: {
    color: "#AAA",
  },
  arrowButton: {
    width: 60,
    height: 60,
    backgroundColor: "#F5F5F5",
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",

    // iOS shadow
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 6,

    // Android shadow
    elevation: 4,
  },

  arrowIcon: {
    width: "50%",
    height: "50%",
  },

  rangeText: {
    fontSize: 36,
    fontWeight: "500",
    marginHorizontal: 20,
  },
});
